import { SchemasSettings } from 'monaco-yaml';
import { CWTSchema, CWTSchemaPropertySummary, CWTSchemaField, CWTSchemaTable } from '../../../types/custom-worksheet-v2';
import { isInCollection } from '../store/utils';

export function buildSchema(
    schema: CWTSchema,
    currentField: CWTSchemaField | CWTSchemaPropertySummary | CWTSchemaTable,
    references: string[]
): SchemasSettings[] {
    const allowListItemRef = Boolean(isInCollection(schema, currentField));

    const standardVals = references.map((ref) => ({ const: `$${ref}` }));
    const selfVals = allowListItemRef ? references.map((ref) => ({ const: `__self__.$${ref}` })) : [];
    const prevVals = allowListItemRef ? references.map((ref) => ({ const: `__prev__.$${ref}` })) : [];
    const nextVals = allowListItemRef ? references.map((ref) => ({ const: `__next__.$${ref}` })) : [];

    const refs = [...standardVals, ...selfVals, ...prevVals, ...nextVals];

    return [
        {
            uri: 'http://example.com/schema-name.json',
            fileMatch: ['*.yaml'],
            schema: {
                $id: 'calculation.schema.json',
                title: 'Calculation',
                description: 'A custom worksheet field calculation',
                anyOf: [
                    { $ref: '#/$defs/ref' },
                    { $ref: '#/$defs/expression' },
                    { $ref: '#/$defs/function' },
                    { $ref: '#/$defs/condition' },
                    { $ref: '#/$defs/generator' },
                ],
                $defs: {
                    str: {
                        type: 'string',
                        not: {
                            anyOf: [{ pattern: '^\\$.*$' }, { pattern: '^__.+__\\.\\$.+$' }],
                        },
                    },
                    ref: {
                        oneOf: refs,
                    },
                    expression: {
                        type: 'object',
                        properties: {
                            type: {
                                type: 'string',
                                enum: ['EXPRESSION'],
                                description: 'A simple math expression',
                                examples: [],
                            },
                            return: {
                                type: 'object',
                                properties: {
                                    operation: {
                                        oneOf: [
                                            { const: '*', description: 'Multiply' },
                                            { const: '/', description: 'Divide' },
                                            { const: '+', description: 'Add' },
                                            { const: '-', description: 'Minus' },
                                            { const: '^', description: 'Power' },
                                        ],
                                    },
                                    params: {
                                        type: 'array',
                                        items: {
                                            anyOf: [
                                                { $ref: '#/$defs/ref' },
                                                { type: 'number' },
                                                { $ref: '#/$defs/expression' },
                                                { $ref: '#/$defs/function' },
                                            ],
                                        },
                                        maxItems: 2,
                                        minItems: 2,
                                    },
                                },
                                errorMessage: 'Return must have an operation and params',
                                required: ['operation', 'params'],
                            },
                        },
                        required: ['type', 'return'],
                        additionalProperties: false,
                    },
                    function: {
                        type: 'object',
                        properties: {
                            type: {
                                type: 'string',
                                enum: ['FUNCTION'],
                            },
                            return: {
                                type: 'object',
                                properties: {
                                    operation: {
                                        oneOf: [
                                            { const: 'SUM', description: 'Sum function' },
                                            { const: 'AVERAGE', description: 'Average function' },
                                            { const: 'MAX', description: 'Max function' },
                                            { const: 'MIN', description: 'Min function' },
                                            { const: 'ABS', description: 'Absolute value function' },
                                            { const: 'ROUND', description: 'Round function' },
                                            { const: 'ROUND_UP', description: 'Round up function' },
                                            { const: 'ROUND_DOWN', description: 'Round down function' },
                                            { const: 'CEILING', description: 'Ceiling function' },
                                            { const: 'FLOOR', description: 'Floor function' },
                                            { const: 'COUNT', description: 'Count function' },
                                            { const: 'SQRT', description: 'Square root function' },
                                            { const: 'NEGATIVE', description: 'Negative function' },
                                            { const: 'CONCAT', description: 'Concat function' },
                                            { const: 'DAYS_BETWEEN', description: 'Days between function' },
                                            { const: 'IDAYS_BETWEEN', description: 'Days between function (Inclusive)' },
                                            { const: 'MONTHS_BETWEEN', description: 'Months between function' },
                                            { const: 'YEARS_BETWEEN', description: 'Years between function' },
                                            { const: 'DAY', description: 'Day function' },
                                            { const: 'MONTH', description: 'Month function' },
                                            { const: 'YEAR', description: 'Year function' },
                                            { const: 'ADD_YEARS', description: 'Adds years to a date' },
                                            { const: 'MINUS_YEARS', description: 'Minus years from a date' },
                                            { const: 'ADD_MONTHS', description: 'Adds months to a date' },
                                            { const: 'MINUS_MONTHS', description: 'Minus months from a date' },
                                            { const: 'ADD_DAYS', description: 'Adds days to a date' },
                                            { const: 'MINUS_DAYS', description: 'Minus days from a date' },
                                            { const: 'DATE', description: 'Construct a date from year, month, day' },
                                            { const: 'EOMONTH', description: 'Get the date at the end of the month' },
                                            { const: 'IS_NULL', description: 'Returns true if a value is null otherwise false' },
                                            {
                                                const: 'SUM_LOANINT',
                                                description:
                                                    'Returns the sum of the total of interest on a principal loan (p) based on an interest rate of (i), for a term of (n) and repayments of (y).\n Parameters:\n- Principal loan\n-Interest rate\n-Term\n-Repayments\n-Type 0 (repayments in arrears) OR 1 (repayments in advanced)',
                                            },
                                            {
                                                const: 'SUM_CBAL',
                                                description:
                                                    'Returns the closing balance on a principal loan (p) based on an interest rate of (i), for a term of (n) and repayments of (y)\n Parameters:\n- Principal loan\n-Interest rate\n-Term\n-Repayments\n-Type 0 (repayments in arrears) OR 1 (repayments in advanced)',
                                            },
                                            {
                                                const: 'FINANCIAL_YEAR_END',
                                                description:
                                                    'Calculates the current financial year given a date, FY period end month and FY period end day.\n Parameters:\n - Date (YYYY-MM-DD) or date field reference\n- FY end month\n- FY end day',
                                                examples: ['[2023-06-30,6,30] -> 2023-06-30 | [2023-07-01,6,30] -> 2024-06-30'],
                                            },
                                            {
                                                const: 'TAX_AU',
                                                description:
                                                    'Calculates income tax for a specific date.\nhttps://www.ato.gov.au/tax-rates-and-codes/tax-rates-australian-residents \n**NOTE**: This only applies to the years 2025 - 2013. Any date outside these bounds will default to 0.\nParameters:\n - Amount in cents or field reference\n- Date (YYYY-MM-DD) or date field reference',
                                            },
                                            {
                                                const: 'DIV_7A_RATE',
                                                description:
                                                    'Returns the benchmark interest rate for a division 7a loan for a given date.\nhttps://www.ato.gov.au/tax-rates-and-codes/division-7a-benchmark-interest-rate#ato-Benchmarkinterestrates\n**NOTE**: This only applies to the years 2025 - 2015. Any date greater than 2025 will use its rate. Any date before 2015 will default to 0\nParameters:\n- Date (YYYY-MM-DD) or date field reference',
                                            },
                                            {
                                                const: 'RATE',
                                                description:
                                                    'Returns the interest rate per period of an annuity.\n Parameters\n- nper: The total number of payment periods in an annuity.\n- pmt: The payment made each period and cannot change over the life of the annuity. Typically, pmt includes principal and interest but no other fees or taxes.\n- pv: The present value — the total amount that a series of future payments is worth now.\n- fv: The future value, or a cash balance you want to attain after the last payment is made.\n- type: The number 0 or 1 and indicates when payments are due.\n- guess: Your guess for what the rate will be.',
                                            },
                                            {
                                                const: 'PMT',
                                                description:
                                                    'Returns the payment for a loan based on constant payments and a constant interest rate.\n Parameters\n- rate: The interest rate for the loan.\n- nper: The total number of payments for the loan.\n- pv: The present value, or the total amount that a series of future payments is worth now; also known as the principal.\n- fv:  Optional. The future value, or a cash balance you want to attain after the last payment is made. If fv is omitted, it is assumed to be 0 (zero), that is, the future value of a loan is 0.\n- type: Optional. The number 0 (zero) or 1 and indicates when payments are due.',
                                            },
                                            {
                                                const: 'PPMT',
                                                description:
                                                    'Returns the payment on the principal for a given period for an investment based on periodic, constant payments and a constant interest rate.\n Parameters:\n- rate: The interest rate per period.\n- per: Specifies the period and must be in the range 1 to nper.\n- nper: The total number of payment periods in an annuity.\n- pv: The present value — the total amount that a series of future payments is worth now.\n- fv: Optional. The future value, or a cash balance you want to attain after the last payment is made. If fv is omitted, it is assumed to be 0 (zero), that is, the future value of a loan is 0.\n- type: The number 0 or 1 and indicates when payments are due.',
                                            },
                                            {
                                                const: 'WORKPAPER',
                                                description:
                                                    "Returns a property on the workpaper this calculation is running in.\n\nNOTE: Currently only the param 'periodEnd' is supported.",
                                            },
                                            {
                                                const: 'ENTITY',
                                                description:
                                                    "Returns a property on the entity this calculation is running in.\n\nNOTE: Currently only the param 'financialYearEndMonth' and 'financialYearEndDay' is supported.",
                                            },
                                            {
                                                const: 'ATOSA_ACTIVITY_STMT_CODE',
                                                description:
                                                    "Gets a totalled value for a monetary activity statement fact.\n\n Parameters\ninterval: The interval to pull the fact. 'A' (Annual) 'Q' (Quarter) 'M' (Month),\n- endDate: YYYY-MM-DD financial end date. The financial year start date will be computed automatically.\n- code: Activity statement fact code e.g. G1\n- fyEndMonth: Financial year end month\n- fyEndDay: Financial year end day.\n- period: Numeric period specific to the interval. For interval 'A' this is ignored",
                                            },
                                            {
                                                const: 'ATOSA_ACTIVITY_STMT_CODE_ANNUAL',
                                                description:
                                                    'Gets a totalled value for a monetary activity statement fact.\n\n Parameters\n- endDate: YYYY-MM-DD financial end date. The financial year start date will be computed automatically.\n- code: Activity statement fact code e.g. G1\n- fyEndMonth: Financial year end month\n- fyEndDay: Financial year end day',
                                            },
                                            {
                                                const: 'ATOSA_ACTIVITY_STMT_CODE_QTR',
                                                description:
                                                    'Gets a totalled value for a monetary activity statement fact for a given quarter.\n\n Parameters\n- endDate: YYYY-MM-DD financial end date. The financial year start date will be computed automatically.\n- code: Activity statement fact code e.g. G1\n- quarter: 1,2,3 or 4\n- fyEndMonth: Financial year end month\n- fyEndDay: Financial year end day',
                                            },
                                            {
                                                const: 'ATOSA_ACTIVITY_STMT_CODE_MONTH',
                                                description:
                                                    'Gets a totalled value for a monetary activity statement fact for a given month.\n\n Parameters\n- endDate: YYYY-MM-DD financial end date. The financial year start date will be computed automatically.\n- code: Activity statement fact code e.g. G1\n- month: 1-12 (12 being last month for the period) \n- fyEndMonth: Financial year end month\n- fyEndDay: Financial year end day',
                                            },
                                            {
                                                const: 'FIND',
                                                description:
                                                    'Finds a value in a table using a lookup column value. If multiple values are matched, the first is returned.\n\n Parameters\n- tableId: The pointer to the table e.g. $table1\n- matchColumn: The reference the column to match on (DO NOT use the $prefix, just pass in the reference as a string e.g. "col1")\n- matchValue: The value to match on. (This can be a calculation that evaluates to an equals match on the column value)\n- returnColumn: The reference the column to which value to return (DO NOT use the $ prefix, just pass in the reference as a string e.g. "col2")\n\n\n\nExample:\n\n```\ntype: FUNCTION:\nreturn:\n\toperation: "FIND"\n\rparams:\n\r- $table1\n\r- "year"\n\r- $currentYear\n\r- "rate"\n```',
                                            },
                                            {
                                                const: 'FILTER',
                                                description:
                                                    'Finds a value in a table using a lookup column value. If multiple values are matched, the sum is returned for monetary values or the last value for all other types.\n\n Parameters\n- tableId: The pointer to the table e.g. $table1\n- matchColumn: The reference the column to match on (DO NOT use the $prefix, just pass in the reference as a string e.g. "col1")\n- matchValue: The value to match on. (This can be a calculation that evaluates to an equals match on the column value)\n- returnColumn: The reference the column to which value to return (DO NOT use the $ prefix, just pass in the reference as a string e.g. "col2")\n\n\n\nExample:\n\n```\ntype: FUNCTION:\nreturn:\n\toperation: "FILTER"\n\rparams:\n\r- $table1\n\r- "year"\n\r- $currentYear\n\r- "amount"\n```',
                                            },
                                        ],
                                    },
                                    params: {
                                        type: 'array',
                                        minItems: 1,
                                        items: {
                                            anyOf: [
                                                {
                                                    type: 'string',
                                                    oneOf: [{ $ref: '#/$defs/ref' }, { $ref: '#/$defs/str' }],
                                                },
                                                { type: 'number' },
                                                { $ref: '#/$defs/expression' },
                                                { $ref: '#/$defs/function' },
                                            ],
                                        },
                                    },
                                },
                                required: ['operation', 'params'],
                            },
                        },
                        required: ['return', 'type'],
                    },
                    generator: {
                        type: 'object',
                        properties: {
                            type: {
                                type: 'string',
                                enum: ['GENERATOR'],
                            },
                            return: {
                                type: 'object',
                                properties: {
                                    operation: {
                                        oneOf: [
                                            {
                                                const: 'ATOSA_ACTIVITY_STMT',
                                                description:
                                                    "Generate a table showing an activity statement broken down monthly or quarterly\n\nParameters:\ninterval: 'Q' (Quarterly) or 'M' (Monthly)\n- endDate: period end date\n- financialYearEndMonth: Financial year end month\n- financialYearEndDay: Financial year end day",
                                            },
                                            {
                                                const: 'ATOSA_ACTIVITY_STMT_MONTHLY',
                                                description:
                                                    'Generate a table showing an activity statement broken down monthly\n\nParameters:\n- endDate: period end date\n- financialYearEndMonth: Financial year end month\n- financialYearEndDay: Financial year end day',
                                            },

                                            {
                                                const: 'ATOSA_ACTIVITY_STMT_QUARTERLY',
                                                description:
                                                    'Generate a table showing an activity statement broken down quarterly\n\nParameters:\n- endDate: period end date\n- financialYearEndMonth: Financial year end month\n- financialYearEndDay: Financial year end day',
                                            },
                                        ],
                                    },
                                    params: {
                                        type: 'array',
                                        minItems: 1,
                                        items: {
                                            anyOf: [
                                                {
                                                    type: 'string',
                                                    oneOf: [{ $ref: '#/$defs/ref' }, { $ref: '#/$defs/str' }],
                                                },
                                                { type: 'number' },
                                                { $ref: '#/$defs/expression' },
                                                { $ref: '#/$defs/function' },
                                            ],
                                        },
                                    },
                                },
                                required: ['operation', 'params'],
                            },
                        },
                        required: ['return', 'type'],
                    },
                    check: {
                        type: 'object',
                        properties: {
                            lhs: {
                                oneOf: [
                                    { $ref: '#/$defs/str' },
                                    { type: 'number' },
                                    { type: 'boolean' },
                                    { $ref: '#/$defs/ref' },
                                    { $ref: '#/$defs/function' },
                                    { $ref: '#/$defs/expression' },
                                    { $ref: '#/$defs/condition' },
                                ],
                            },
                            operation: { enum: ['>', '>=', '<', '<=', '=', '!='] },
                            rhs: {
                                oneOf: [
                                    { $ref: '#/$defs/str' },
                                    { type: 'number' },
                                    { type: 'boolean' },
                                    { $ref: '#/$defs/ref' },
                                    { $ref: '#/$defs/function' },
                                    { $ref: '#/$defs/expression' },
                                    { $ref: '#/$defs/condition' },
                                ],
                            },
                        },
                        required: ['lhs', 'operation', 'rhs'],
                    },
                    and: {
                        type: 'object',
                        properties: {
                            and: {
                                type: 'array',
                                items: {
                                    anyOf: [{ $ref: '#/$defs/check' }, { $ref: '#/$defs/and' }, { $ref: '#/$defs/or' }],
                                },
                                minItems: 2,
                            },
                        },
                        required: ['and'],
                    },
                    or: {
                        type: 'object',
                        properties: {
                            or: {
                                type: 'array',
                                items: {
                                    anyOf: [{ $ref: '#/$defs/check' }, { $ref: '#/$defs/and' }, { $ref: '#/$defs/or' }],
                                },
                                minItems: 2,
                            },
                        },
                        required: ['or'],
                    },
                    if: {
                        type: 'object',
                        oneOf: [{ $ref: '#/$defs/and' }, { $ref: '#/$defs/or' }, { $ref: '#/$defs/check' }],
                    },
                    block: {
                        type: 'object',
                        properties: {
                            return: {
                                anyOf: [
                                    { $ref: '#/$defs/str' },
                                    { $ref: '#/$defs/ref' },
                                    { type: 'boolean' },
                                    { type: 'number' },
                                    { type: 'null' },
                                    { $ref: '#/$defs/function' },
                                    { $ref: '#/$defs/expression' },
                                    { $ref: '#/$defs/condition' },
                                ],
                            },
                        },
                        required: ['return'],
                    },
                    condition: {
                        type: 'object',
                        properties: {
                            type: {
                                type: 'string',
                                enum: ['CONDITION'],
                                description: 'A condition',
                                examples: [],
                            },
                            return: {
                                oneOf: [
                                    {
                                        type: 'object',
                                        properties: {
                                            if: { $ref: '#/$defs/if' },
                                            then: { $ref: '#/$defs/block' },
                                            else: { $ref: '#/$defs/block' },
                                        },
                                        required: ['if', 'then', 'else'],
                                    },
                                ],
                            },
                        },
                        required: ['type', 'return'],
                    },
                },
            },
        },
    ];
}
